import { BaseInfoContext } from '@/context/BaseInfoContext';
import { TEMP_USER_NAME, useFindUser, User } from '@/models/user';
import { playSound } from '@/utils/sound';
import { getUserAvatar } from '@/utils/uiHelper';
import { ShareAltOutlined } from '@ant-design/icons';
import { Space, Tag } from 'antd';
import { AvatarProps } from 'antd/es/avatar';
import { SpaceProps } from 'antd/es/space';
import React, { useContext, useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import UserGender from './Gender';
import UserName, { MobileDisplayMode } from './Name';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@/models/common';

export type Props = {
  user: User;
  /**展示上下游关系 */
  showFromUser?: boolean;
  showGender?: boolean;
  showRemarkName?: boolean;
  avatarProps?: AvatarProps;
  spaceProps?: SpaceProps;
  showNickName?: boolean;
  showMobile?: MobileDisplayMode;
};

/**
 * 用户信息展示
 * @param params.user 用户，当缺少 addTime 字段时，认为信息不充分，将拉取远程数据
 */
const UserIntro: React.FC<Props> = ({
  user: userProp,
  showNickName = true,
  showFromUser = true,
  showGender = true,
  showRemarkName = true,
  avatarProps = { size: 'large' },
  spaceProps = { direction: 'vertical', size: 5 },
  showMobile,
}: Props) => {
  const [{}, , helpers] = useContext(BaseInfoContext);
  const { sendByUser: fetchUserInfo } = useFindUser();
  const [user, updateUser] = useImmer<User>(null);
  const lastRequestedRef = useRef<Partial<User>>({ id: null, username: 'N/A' });

  useEffect(() => {
    if (!userProp.addTime) {
      // 用户信息不完整，调用远程数据

      if (lastRequestedRef.current?.id === userProp.id || lastRequestedRef.current?.username === userProp.username) {
        // 避免重复调用相同的用户信息
        return;
      }

      lastRequestedRef.current = { id: userProp.id, username: userProp.username };
      if (userProp.id === -1) {
        // 临时用户，不请求远程数据
        updateUser(() => ({ ...userProp, nickname: TEMP_USER_NAME, addTime: dayjs().format(DateTimeFormat) }));
        return;
      }
      fetchUserInfo(userProp).then(updatedUser => updateUser(() => updatedUser));
    } else {
      // 用户信息完整，直接使用 userProp
      updateUser(() => userProp);
    }
  }, [userProp, fetchUserInfo]);

  if (!user) return null;

  return (
    <Space direction="vertical" size={5}>
      <Space
        {...spaceProps}
        onDoubleClick={e => {
          e.stopPropagation();
          playSound('click');
          helpers.showUserModal({
            userId: user.id,
          });
        }}>
        <div>{getUserAvatar(user, avatarProps)}</div>
        <div
          style={{
            paddingTop: spaceProps.direction === 'vertical' ? 0 : 0,
            wordBreak: 'break-all',
            display: 'flex',
          }}>
          <>
            {showGender && <UserGender gender={user.gender} />}
            {` `}
            <UserName
              user={user}
              spaceProps={{ direction: spaceProps.direction }}
              showRemarkName={showRemarkName}
              showNickName={showNickName}
              showMobile={showMobile}
            />
          </>
        </div>
      </Space>
      {showFromUser && user.fromUserId && user.fromUserId > 0 ? (
        <Tag
          icon={<ShareAltOutlined />}
          style={{ userSelect: 'none' }}
          onDoubleClick={e => {
            e.stopPropagation();
            helpers.showUserModal({
              userId: user.fromUserId,
            });
          }}>
          {user.fromUserId}
        </Tag>
      ) : null}
    </Space>
  );
};

export default UserIntro;
